import React, { useState } from 'react';
import axios from 'axios';
import { X, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from './ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtMoney = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const EscrowCorrectionModal = ({ row, driverName, onClose, onCorrected }) => {
  const [amount, setAmount] = useState(String(row.amount_collected));
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const maxDue = row.original ? row.original.amount_due : row.amount_due;
  const newVal = Number(amount);
  const delta = Math.round((newVal - row.amount_collected) * 100) / 100;

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!Number.isFinite(newVal) || newVal < 0) return setError('Enter a valid amount of $0.00 or more.');
    if (newVal > maxDue + 0.005) return setError(`Amount cannot exceed the ${fmtMoney(maxDue)} scheduled for this period.`);
    if (Math.abs(delta) < 0.005 && Math.abs(newVal - row.amount_due) < 0.005) return setError('This period already shows this amount with nothing deferred.');
    if (reason.trim().length < 3) return setError('A reason is required (at least 3 characters).');
    setSaving(true);
    try {
      const res = await axios.post(`${API}/escrow/ledger/${row.id}/correct`, { new_collected: newVal, reason: reason.trim() }, { withCredentials: true });
      toast.success(res.data.draft
        ? 'Correction recorded and the unsent draft was rebuilt with the corrected escrow. Nothing is applied to the driver\'s balance until the payroll is sent.'
        : res.data.payroll_locked
          ? 'Correction recorded. Payroll is locked — statement and payment unchanged; escrow balance updated.'
          : 'Escrow corrected. Payroll totals updated.');
      onCorrected(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Correction failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" data-testid="escrow-correction-modal">
      <form onSubmit={submit} noValidate className="w-full max-w-md rounded-xl bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Correct escrow deduction</h2>
            <p className="text-xs text-slate-500">{driverName} · {row.period_start} → {row.period_end}</p>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="escrow-correction-close"><X className="h-5 w-5" /></button>
        </div>
        <div className="space-y-4 px-5 py-4 text-sm">
          <div className="grid grid-cols-3 gap-2 rounded-lg bg-slate-50 p-3 text-xs">
            <div><p className="text-slate-500">Scheduled</p><p className="font-semibold">{fmtMoney(maxDue)}</p></div>
            <div><p className="text-slate-500">Collected</p><p className="font-semibold" data-testid="escrow-correction-current">{fmtMoney(row.amount_collected)}</p></div>
            <div><p className="text-slate-500">Deferred</p><p className="font-semibold">{fmtMoney(row.amount_deferred)}</p></div>
          </div>
          {row.payroll_locked && (
            <div className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="escrow-correction-locked-note">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span>This payroll is locked. The correction is recorded and the driver's escrow balance is updated, but the finalized statement and the issued payment are not changed. Any money owed back requires a separate refund decision.</span>
            </div>
          )}
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">Corrected collected amount</span>
            <input type="number" step="0.01" min="0" max={maxDue} value={amount} onChange={(e) => setAmount(e.target.value)}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" data-testid="escrow-correction-amount" />
            <div className="mt-1 flex items-center justify-between text-xs text-slate-500">
              <button type="button" onClick={() => setAmount('0')} className="underline" data-testid="escrow-correction-set-zero">Set to $0 (remove this week's escrow)</button>
              {Number.isFinite(newVal) && <span data-testid="escrow-correction-delta">Change: {delta >= 0 ? '+' : '−'}{fmtMoney(Math.abs(delta))}</span>}
            </div>
          </label>
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">Reason (required)</span>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={500}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" placeholder="e.g. Driver had $0 earnings this week — waive escrow" data-testid="escrow-correction-reason" />
          </label>
          <p className="text-xs text-slate-500">The difference for this week is waived — it is not deferred to a later payroll. No refund is issued.</p>
          {error && <p className="text-xs text-red-600" data-testid="escrow-correction-error">{error}</p>}
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-3">
          <button type="button" onClick={onClose} className="rounded-lg px-4 py-2 text-sm text-slate-600 hover:bg-slate-100" data-testid="escrow-correction-cancel">Cancel</button>
          <button type="submit" disabled={saving} className="flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-700 disabled:opacity-50" data-testid="escrow-correction-submit">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}Record correction
          </button>
        </div>
      </form>
    </div>
  );
};
